import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ChatMessage, WebhookRequest, WebhookResponse } from '../models/chat-proveedor.models';
import { getCurrentUser } from 'src/app/core/services/functions.service';
import { AsignarProveedorService } from '../services/asignar-proveedor.service';

interface ChatSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  lastMessage: Date;
  createdAt: Date;
}

@Component({
  selector: 'app-chat-proveedor',
  templateUrl: './chat-proveedor.component.html',
  styleUrls: ['./chat-proveedor.component.scss']
})
export class ChatProveedorComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messagesContainer') messagesContainer!: ElementRef;
  @ViewChild('messageInput') messageInput!: ElementRef;

  messages: ChatMessage[] = [];
  messageControl = new FormControl('', [Validators.required, Validators.maxLength(1000)]);
  isTyping = false;
  isSending = false;
  usuario: string = '';
  sidebarCollapsed = false;
  currentChatId = '';
  chatSessions: ChatSession[] = [];
  todayChats: ChatSession[] = [];
  olderChats: ChatSession[] = [];
  isRecording = false;
  
  private destroy$ = new Subject<void>();
  private shouldScrollToBottom = false;
  private sessionId = '';
  private recognition: any;
  private speechSupported = false;

  quickSuggestions = [
    'Deseo asignar un proveedor',
    'Dime los pasos para asignar un proveedor'
  ];

  constructor(private asignarProveedorService: AsignarProveedorService) {}

  ngOnInit(): void {
    this.usuario = getCurrentUser().usuario_aplicativo || '';
    this.initializeSpeechRecognition();
    this.loadChatSessions();
    this.createNewChat();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  private initializeChat(): void {
    const welcomeMessage: ChatMessage = {
      id: 'msg_' + Date.now(),
      content: '¡Hola! Soy tu asistente para asignación de proveedores. ¿En qué puedo ayudarte hoy?',
      timestamp: new Date(),
      isUser: false,
      status: 'sent'
    };
    this.messages.push(welcomeMessage);
    this.shouldScrollToBottom = true;
  }

  sendMessage(): void {
    if (this.messageControl.valid && !this.isSending) {
      const content = this.messageControl.value?.trim();
      if (content) {
        const userMessage: ChatMessage = {
          id: 'msg_' + Date.now(),
          content,
          timestamp: new Date(),
          isUser: true,
          status: 'sending'
        };

        this.messages.push(userMessage);
        this.messageControl.reset();
        this.isSending = true;
        this.isTyping = true;
        this.shouldScrollToBottom = true;

        this.asignarProveedorService.sendChatMessage(this.sessionId, content)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              userMessage.status = 'sent';
              this.isTyping = false;
              this.isSending = false;
              
              const botMessage: ChatMessage = {
                id: 'msg_' + Date.now(),
                content: response.output || 'Lo siento, no pude procesar tu mensaje.',
                timestamp: new Date(),
                isUser: false,
                status: 'sent'
              };
              
              this.messages.push(botMessage);
              this.updateCurrentChat();
              this.shouldScrollToBottom = true;
              this.focusInput();
            },
            error: () => {
              userMessage.status = 'error';
              this.isTyping = false;
              this.isSending = false;
              
              const errorMessage: ChatMessage = {
                id: 'msg_' + Date.now(),
                content: 'Lo siento, hubo un error. Por favor, intenta nuevamente.',
                timestamp: new Date(),
                isUser: false,
                status: 'sent'
              };
              
              this.messages.push(errorMessage);
              this.updateCurrentChat();
              this.shouldScrollToBottom = true;
              this.focusInput();
            }
          });
      }
    }
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  selectSuggestion(suggestion: string): void {
    this.messageControl.setValue(suggestion);
    this.sendMessage();
  }

  clearChat(): void {
    this.messages = [];
    this.updateCurrentChat();
    this.initializeChat();
  }

  private scrollToBottom(): void {
    try {
      if (this.messagesContainer) {
        const element = this.messagesContainer.nativeElement;
        element.scrollTop = element.scrollHeight;
      }
    } catch (err) {}
  }

  private focusInput(): void {
    setTimeout(() => {
      if (this.messageInput) {
        this.messageInput.nativeElement.focus();
      }
    }, 100);
  }

  getMessageTime(timestamp: Date): string {
    const now = new Date();
    const messageTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now.getTime() - messageTime.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Ahora';
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
    return messageTime.toLocaleDateString();
  }

  getMessageStatusIcon(message: ChatMessage): string {
    if (!message.isUser) return '';
    switch (message.status) {
      case 'sending': return 'access_time';
      case 'sent': return 'done';
      case 'error': return 'error_outline';
      default: return '';
    }
  }

  toggleSidebar(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  createNewChat(): void {
    const newChatId = this.usuario;
    const newChat: ChatSession = {
      id: newChatId,
      title: 'Nuevo Chat',
      messages: [],
      lastMessage: new Date(),
      createdAt: new Date()
    };
    
    this.chatSessions.unshift(newChat);
    this.currentChatId = newChatId;
    this.messages = [];
    this.sessionId = newChatId;
    this.initializeChat();
    this.updateCurrentChat();
    this.organizeChatsByDate();
  }

  selectChat(chatId: string): void {
    const chat = this.chatSessions.find(c => c.id === chatId);
    if (chat) {
      this.currentChatId = chatId;
      this.sessionId = chatId;
      this.messages = [...chat.messages];
      this.shouldScrollToBottom = true;
    }
  }

  deleteChat(chatId: string, event: Event): void {
    event.stopPropagation();
    this.chatSessions = this.chatSessions.filter(c => c.id !== chatId);
    
    if (this.currentChatId === chatId) {
      if (this.chatSessions.length > 0) {
        this.selectChat(this.chatSessions[0].id);
      } else {
        this.createNewChat();
      }
    }
    
    this.saveChatSessions();
    this.organizeChatsByDate();
  }

  getRelativeTime(date: Date): string {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Ahora';
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
    return date.toLocaleDateString();
  }

  private updateCurrentChat(): void {
    const currentChat = this.chatSessions.find(c => c.id === this.currentChatId);
    if (currentChat) {
      currentChat.messages = [...this.messages];
      currentChat.lastMessage = new Date();
      
      if (currentChat.title === 'Nuevo Chat') {
        const firstUserMessage = this.messages.find(m => m.isUser);
        if (firstUserMessage) {
          currentChat.title = firstUserMessage.content.substring(0, 30) + (firstUserMessage.content.length > 30 ? '...' : '');
        }
      }
      
      this.saveChatSessions();
      this.organizeChatsByDate();
    }
  }

  private loadChatSessions(): void {
    const sessionsKey = `proveedor-chat-sessions-${this.usuario}`;
    const savedSessions = localStorage.getItem(sessionsKey);
    if (savedSessions) {
      try {
        const parsedSessions = JSON.parse(savedSessions);
        this.chatSessions = parsedSessions.map((session: any) => ({
          ...session,
          lastMessage: new Date(session.lastMessage),
          createdAt: new Date(session.createdAt),
          messages: session.messages.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }))
        }));
        this.organizeChatsByDate();
      } catch (error) {
        console.error('Error loading chat sessions:', error);
      }
    }
  }

  private saveChatSessions(): void {
    const sessionsKey = `proveedor-chat-sessions-${this.usuario}`;
    try {
      localStorage.setItem(sessionsKey, JSON.stringify(this.chatSessions));
    } catch (error) {
      console.error('Error saving chat sessions:', error);
    }
  }

  private organizeChatsByDate(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    this.todayChats = this.chatSessions.filter(chat => {
      const chatDate = new Date(chat.lastMessage);
      chatDate.setHours(0, 0, 0, 0);
      return chatDate.getTime() === today.getTime();
    });
    
    this.olderChats = this.chatSessions.filter(chat => {
      const chatDate = new Date(chat.lastMessage);
      chatDate.setHours(0, 0, 0, 0);
      return chatDate.getTime() < today.getTime();
    });
  }

  toggleVoiceRecording(): void {
    if (!this.speechSupported) {
      alert('Tu navegador no soporta reconocimiento de voz. Intenta con Chrome o Edge.');
      return;
    }

    if (this.isRecording) {
      this.stopRecording();
    } else {
      this.startRecording();
    }
  }

  private initializeSpeechRecognition(): void {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      this.speechSupported = true;
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = false;
      this.recognition.interimResults = false;
      this.recognition.lang = 'es-ES';
      
      this.recognition.onstart = () => {
        this.isRecording = true;
      };
      
      this.recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        this.messageControl.setValue(transcript);
        this.isRecording = false;
      };
      
      this.recognition.onerror = (event: any) => {
        console.error('Error en reconocimiento de voz:', event.error);
        this.isRecording = false;
        if (event.error === 'not-allowed') {
          alert('Permiso de micrófono denegado. Por favor, permite el acceso al micrófono.');
        }
      };
      
      this.recognition.onend = () => {
        this.isRecording = false;
      };
    }
  }

  private startRecording(): void {
    try {
      this.recognition.start();
    } catch (error) {
      console.error('Error al iniciar grabación:', error);
      this.isRecording = false;
    }
  }

  private stopRecording(): void {
    try {
      this.recognition.stop();
    } catch (error) {
      console.error('Error al detener grabación:', error);
    }
    this.isRecording = false;
  }
}